interface StoredEventSlot {
  activeSlotId: string;
  lastSeenInR3labs: number;
  slotData: {
    event_id: string;
    event_name: string;
    event_slug: string;
    event_image_id: string;
    active_slot_start_datetime: string;
    active_slot_end_datetime: string;
    ondeck_channel_name: string;
    ondeck_channel_platform_user_id: string;
  };
}

export class EventSlotManager {
  private static readonly STORAGE_KEY = 'event_slots';
  private static readonly GRACE_PERIOD_MS = 10 * 60 * 1000; // 10 minutes in milliseconds

  private static getStoredSlots(): StoredEventSlot[] {
    const stored = localStorage.getItem(EventSlotManager.STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Failed to load event slots:', error);
        return [];
      }
    }
    return [];
  }

  private static saveStoredSlots(slots: StoredEventSlot[]): void {
    localStorage.setItem(EventSlotManager.STORAGE_KEY, JSON.stringify(slots));
  }

  public static storeActiveSlot(
    activeSlotId: string,
    slotData: StoredEventSlot['slotData']
  ): void {
    console.log('Storing active slot:', { activeSlotId, slotData });
    const slots = this.getStoredSlots();
    const now = Date.now();
    const existingIndex = slots.findIndex(slot => slot.activeSlotId === activeSlotId);
    
    if (existingIndex !== -1) {
      slots[existingIndex].lastSeenInR3labs = now;
      slots[existingIndex].slotData = slotData;
    } else {
      slots.push({
        activeSlotId,
        lastSeenInR3labs: now,
        slotData
      });
    }

    this.saveStoredSlots(slots);
  }

  public static isSlotActive(activeSlotId: string): boolean {
    const slots = this.getStoredSlots();
    const slot = slots.find(s => s.activeSlotId === activeSlotId);
    
    if (!slot) return false;
    
    const now = Date.now();
    const timeSinceLastSeen = now - slot.lastSeenInR3labs;
    return timeSinceLastSeen <= this.GRACE_PERIOD_MS;
  }

  public static getSlotData(activeSlotId: string): StoredEventSlot['slotData'] | null {
    const slots = this.getStoredSlots();
    const slot = slots.find(s => s.activeSlotId === activeSlotId);
    return slot?.slotData || null;
  }

  public static getLastActiveSlot(): StoredEventSlot['slotData'] | null {
    const slots = this.getStoredSlots();
    const now = Date.now();
    const validSlots = slots.filter(slot => {
      const timeSinceLastSeen = now - slot.lastSeenInR3labs;
      return timeSinceLastSeen <= this.GRACE_PERIOD_MS;
    });

    if (validSlots.length === 0) return null;

    validSlots.sort((a, b) => b.lastSeenInR3labs - a.lastSeenInR3labs);
    return validSlots[0].slotData;
  }

  public static cleanupOldSlots(): void {
    const slots = this.getStoredSlots();
    const now = Date.now();
    
    const validSlots = slots.filter(slot => {
      const timeSinceLastSeen = now - slot.lastSeenInR3labs;
      return timeSinceLastSeen <= this.GRACE_PERIOD_MS;
    });
    
    if (slots.length !== validSlots.length) {
      console.log(`EventSlotManager: Cleaned up ${slots.length - validSlots.length} expired slots`);
    }
    
    this.saveStoredSlots(validSlots);
  }
}
